// src/components/FolderBreadcrumb/FolderBreadcrumb.jsx
import React from 'react';
import { useIntl } from 'react-intl';
import { Breadcrumb } from '@zenqms/zui';
import { measureTextWidth } from '../../utils/measureTextWidth';
import './FolderBreadcrumb.scss';

/**
 * @typedef {{ id: string, name: string }} FolderPathItem
 *
 * @param {{
 *   folderPath: FolderPathItem[],
 *   onNavigate: (folderId: string) => void,
 * }} props
 */

/**
 * Max label width in px — kept in sync with --zui-breadcrumb-label-max-width in FolderBreadcrumb.scss.
 * Note: DOM-based truncation detection (scrollWidth vs clientWidth) is not used here because
 * the ZUI Breadcrumb component owns the DOM nodes and does not expose refs to individual labels.
 * The character-count heuristic via measureTextWidth (shared with the ui workspace) is the
 * practical alternative — see src/utils/measureTextWidth.js.
 */
const LABEL_MAX_WIDTH_PX = 250;

/** Returns the name as tooltip title only when it would be visually truncated. */
function titleIfTruncated(name) {
  return measureTextWidth(name) > LABEL_MAX_WIDTH_PX ? name : undefined;
}

export function FolderBreadcrumb({ folderPath, onNavigate }) {
  const { formatMessage } = useIntl();

  const rootAriaLabel = formatMessage({ id: 'folderBreadcrumb.root', defaultMessage: 'Shared folder root' });
  const ellipsisLabel = formatMessage({ id: 'folderBreadcrumb.ellipsis', defaultMessage: 'More folders' });

  const rootItem = (onClick) => ({
    id: 'root',
    icon: 'folder',
    ariaLabel: rootAriaLabel,
    ...(onClick ? { onClick } : {}),
  });

  // Loading — path not yet fetched or not yet an array
  if (!Array.isArray(folderPath) || folderPath.length === 0) {
    return (
      <div className="folder-breadcrumb">
        <Breadcrumb items={[rootItem()]} gap="sm" />
      </div>
    );
  }

  // Short paths (≤3 items): show all
  if (folderPath.length <= 3) {
    const lastFolder = folderPath[folderPath.length - 1];
    const items = [
      rootItem(() => onNavigate(folderPath[0].id)),
      ...folderPath.slice(0, -1).map((item) => ({
        id: item.id,
        label: item.name,
        title: titleIfTruncated(item.name),
        onClick: () => onNavigate(item.id),
      })),
      {
        id: lastFolder.id,
        label: lastFolder.name,
        title: titleIfTruncated(lastFolder.name),
      },
    ];
    return (
      <div className="folder-breadcrumb">
        <Breadcrumb items={items} gap="sm" />
      </div>
    );
  }

  // Deep paths (4+ items): [house] > [•••] > parent > current
  const intermediates = folderPath.slice(1, -2);
  const parent = folderPath[folderPath.length - 2];
  const current = folderPath[folderPath.length - 1];

  const ellipsisItem = {
    id: 'ellipsis',
    icon: 'ellipsis',
    ariaLabel: ellipsisLabel,
    actionMenu: {
      groups: [
        {
          items: intermediates.map((item) => ({
            id: item.id,
            label: item.name,
            onSelect: () => onNavigate(item.id),
          })),
        },
      ],
    },
  };

  const items = [
    rootItem(() => onNavigate(folderPath[0].id)),
    ellipsisItem,
    {
      id: parent.id,
      label: parent.name,
      title: titleIfTruncated(parent.name),
      onClick: () => onNavigate(parent.id),
    },
    {
      id: current.id,
      label: current.name,
      title: titleIfTruncated(current.name),
    },
  ];

  return (
    <div className="folder-breadcrumb">
      <Breadcrumb items={items} gap="sm" />
    </div>
  );
}
